import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import { getToken } from "../services/auth";
import { jwtDecode } from "jwt-decode";
import Sidebar from "../components/Sidebar";
import { motion, AnimatePresence } from "framer-motion";
import {
  Users, UserPlus, Trash2, TrendingUp, BookOpen, Trophy,
  Library, Loader2, AlertCircle, CheckCircle2,
  XCircle, BarChart3, GraduationCap,
  CalendarDays, Monitor, CreditCard, MessageSquareQuote, BrainCircuit
} from "lucide-react";
import toast, { Toaster } from "react-hot-toast";

/* ─── tiny circular progress ─── */
function CircularProgress({ pct, size = 80, stroke = 6, color = "#6366f1" }) {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const off = circ - (pct / 100) * circ;
  return (
    <svg width={size} height={size} className="transform -rotate-90">
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#e2e8f0" strokeWidth={stroke}/>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color}
        strokeWidth={stroke} strokeDasharray={circ} strokeDashoffset={off}
        strokeLinecap="round" className="transition-all duration-700"/>
      <text x="50%" y="50%" dominantBaseline="central" textAnchor="middle"
        className="fill-slate-900 dark:fill-white text-sm font-black"
        transform={`rotate(90 ${size/2} ${size/2})`}>{Math.round(pct)}%</text>
    </svg>
  );
}

/* ─── stat card ─── */
function StatCard({ icon: Icon, label, value, color, bg }) {
  return (
    <motion.div whileHover={{ y: -4 }}
      className={`p-6 ${bg} rounded-[2rem] border border-slate-100 dark:border-slate-800 flex items-center gap-5 shadow-sm`}>
      <div className={`p-4 ${color} text-white rounded-2xl shadow-lg`}><Icon size={22}/></div>
      <div>
        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{label}</p>
        <p className="text-xl font-black text-slate-900 dark:text-white">{value}</p>
      </div>
    </motion.div>
  );
}

export default function ParentDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [children, setChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [progressLoading, setProgressLoading] = useState(false);
  const [linkEmail, setLinkEmail] = useState("");
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkLoading, setLinkLoading] = useState(false);

  const loadChildren = useCallback(async () => {
    try {
      const res = await api.get("/parent/children");
      setChildren(res.data);
      if (res.data.length > 0 && !selectedChild) {
        setSelectedChild(res.data[0]);
      }
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => {
    const token = getToken();
    if (!token) return;
    setUser(jwtDecode(token));
    loadChildren().then(() => setLoading(false));
  }, [loadChildren]);

  useEffect(() => {
    if (!selectedChild) return;
    if (selectedChild.status !== "approved") {
      setProgress(null);
      return;
    }
    setProgressLoading(true);
    api.get(`/parent/child/${selectedChild.student_id}/progress`)
      .then(r => setProgress(r.data))
      .catch(() => toast.error("Failed to load progress"))
      .finally(() => setProgressLoading(false));
  }, [selectedChild]);

  const handleLink = async (e) => {
    e.preventDefault();
    setLinkLoading(true);
    try {
      await api.post("/parent/link", { student_email: linkEmail });
      toast.success("Link request sent! Waiting for admin approval.");
      setLinkEmail("");
      setShowLinkModal(false);
      await loadChildren();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to link student");
    } finally { setLinkLoading(false); }
  };

  const handleUnlink = async (linkId) => {
    if (!window.confirm("Remove this student?")) return;
    try {
      await api.delete(`/parent/link/${linkId}`);
      toast.success("Student removed");
      if (selectedChild?.link_id === linkId) { setSelectedChild(null); setProgress(null); }
      await loadChildren();
    } catch { toast.error("Failed to unlink"); }
  };

  if (loading || !user) return (
    <div className="flex h-screen items-center justify-center dark:bg-slate-900">
      <Loader2 className="text-indigo-600 animate-spin" size={40}/>
    </div>
  );

  const s = progress?.summary;

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 flex font-['Inter',sans-serif]">
      <Toaster position="top-center"/>
      <Sidebar role="parent"/>

      <main className="flex-1 lg:ml-64 p-6 lg:p-10 space-y-10 pb-20">
        {/* ── HEADER ── */}
        <section className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <motion.div initial={{opacity:0,y:20}} animate={{opacity:1,y:0}}>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-violet-50 dark:bg-violet-900/30 text-violet-600 rounded-full text-xs font-black uppercase tracking-widest mb-4">
              <GraduationCap size={14}/> Parent Dashboard
            </div>
            <h1 className="text-4xl lg:text-5xl font-black text-slate-900 dark:text-white tracking-tight">
              Hello, <span className="text-violet-600">{user.email.split("@")[0]}</span>
            </h1>
            <p className="text-slate-500 mt-2 text-lg font-medium">
              Monitor your child's learning journey in real-time.
            </p>
          </motion.div>

          <button onClick={() => setShowLinkModal(true)}
            className="px-8 py-4 bg-violet-600 hover:bg-violet-700 text-white font-black rounded-2xl shadow-xl shadow-violet-200 dark:shadow-none transition-all flex items-center gap-3 self-start">
            <UserPlus size={20}/> Link Student
          </button>
        </section>

        {/* ── CHILDREN SELECTOR ── */}
        {children.length === 0 ? (
          <motion.div initial={{opacity:0}} animate={{opacity:1}}
            className="text-center py-24 bg-slate-50 dark:bg-slate-900 rounded-[2.5rem] border border-dashed border-slate-200 dark:border-slate-800">
            <Users size={48} className="mx-auto text-slate-300 mb-6"/>
            <h3 className="text-xl font-black text-slate-700 dark:text-slate-300">No Students Linked Yet</h3>
            <p className="text-slate-400 text-sm mt-2 max-w-sm mx-auto">
              Click "Link Student" to connect your child's account using their email address.
            </p>
          </motion.div>
        ) : (
          <>
            <section className="flex gap-4 overflow-x-auto pb-2 no-scrollbar">
              {children.map(c => (
                <motion.button key={c.link_id} whileHover={{scale:1.03}} whileTap={{scale:0.97}}
                  onClick={() => setSelectedChild(c)}
                  className={`relative min-w-[200px] p-5 rounded-2xl border-2 transition-all flex items-center gap-4 group ${
                    selectedChild?.student_id === c.student_id
                      ? "border-violet-600 bg-violet-50 dark:bg-violet-900/20 shadow-lg shadow-violet-100 dark:shadow-none"
                      : "border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 hover:border-violet-300"
                  }`}>
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center text-white font-black text-sm">
                    {c.name?.[0]?.toUpperCase() || "S"}
                  </div>
                  <div className="text-left flex-1 min-w-0">
                    <p className="font-black text-sm text-slate-900 dark:text-white truncate">{c.name}</p>
                    <p className="text-[10px] text-slate-400 font-bold truncate">{c.email}</p>
                    <div className={`inline-flex items-center gap-1 mt-1.5 px-2 py-0.5 rounded-full text-[8px] font-black uppercase tracking-wider border
                      ${c.status === 'approved' ? 'bg-emerald-50 text-emerald-600 border-emerald-100 dark:bg-emerald-900/20' : 
                        c.status === 'rejected' ? 'bg-rose-50 text-rose-600 border-rose-100 dark:bg-rose-900/20' : 
                        'bg-amber-50 text-amber-600 border-amber-100 dark:bg-amber-900/20'}`}>
                      {c.status === 'approved' ? <CheckCircle2 size={8} /> : c.status === 'rejected' ? <XCircle size={8} /> : <Loader2 size={8} className="animate-spin" />}
                      {c.status}
                    </div>
                  </div>
                  <button onClick={(e) => {e.stopPropagation(); handleUnlink(c.link_id);}}
                    className="absolute top-2 right-2 p-1.5 rounded-lg text-slate-300 hover:text-rose-500 hover:bg-rose-50 opacity-0 group-hover:opacity-100 transition-all">
                    <Trash2 size={14}/>
                  </button>
                </motion.button>
              ))}
            </section>

            {/* ── PROGRESS REPORT / STATUS FALLBACKS ── */}
            {selectedChild?.status === "pending" ? (
              <motion.div initial={{opacity:0, y:10}} animate={{opacity:1, y:0}}
                className="text-center py-24 bg-amber-50/50 dark:bg-amber-900/10 rounded-[2.5rem] border border-amber-100 dark:border-amber-900/30 flex flex-col items-center">
                <div className="w-16 h-16 bg-amber-100 dark:bg-amber-900/50 rounded-full flex items-center justify-center text-amber-600 mb-6">
                  <Loader2 size={32} className="animate-spin"/>
                </div>
                <h3 className="text-2xl font-black text-slate-900 dark:text-white">Request Pending Approval</h3>
                <p className="text-slate-500 mt-2 max-w-md">
                  We've sent your link request to the site administrators. Once approved, you'll see {selectedChild.name}'s academic progress dashboard here.
                </p>
              </motion.div>
            ) : selectedChild?.status === "rejected" ? (
              <motion.div initial={{opacity:0, y:10}} animate={{opacity:1, y:0}}
                className="text-center py-24 bg-rose-50/50 dark:bg-rose-900/10 rounded-[2.5rem] border border-rose-100 dark:border-rose-900/30 flex flex-col items-center">
                <div className="w-16 h-16 bg-rose-100 dark:bg-rose-900/50 rounded-full flex items-center justify-center text-rose-600 mb-6">
                  <XCircle size={32}/>
                </div>
                <h3 className="text-2xl font-black text-slate-900 dark:text-white">Request Rejected</h3>
                <p className="text-slate-500 mt-2 max-w-md">
                  Unfortunately, the administrators did not approve this link request. Please double-check the email address or contact support if you think this is a mistake.
                </p>
              </motion.div>
            ) : progressLoading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="text-violet-600 animate-spin" size={36}/>
              </div>
            ) : progress && s ? (
              <motion.div initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} className="space-y-10">

                {/* Summary Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
                  <StatCard icon={TrendingUp} label="Video Prog" value={`${s.overall_video_progress}%`} color="bg-indigo-600" bg="bg-slate-50 dark:bg-slate-900"/>
                  <StatCard icon={Trophy} label="Avg Exam" value={`${s.avg_exam_score}%`} color="bg-emerald-500" bg="bg-slate-50 dark:bg-slate-900"/>
                  <StatCard icon={Library} label="Read Rate" value={`${s.avg_book_progress}%`} color="bg-amber-500" bg="bg-slate-50 dark:bg-slate-900"/>
                  
                  <StatCard icon={CalendarDays} label="Live Attn" value={`${s.attendance_count} Sessions`} color="bg-rose-500" bg="bg-slate-50 dark:bg-slate-900"/>
                  <StatCard icon={Monitor} label="Screen Time" value={`${s.screen_time_hours}h`} color="bg-blue-500" bg="bg-slate-50 dark:bg-slate-900"/>
                  <StatCard icon={CreditCard} label="Total Paid" value={`₹${s.total_spent || 0}`} color="bg-teal-600" bg="bg-slate-50 dark:bg-slate-900"/>
                </div>

                {/* Overall Progress Ring */}
                <div className="flex flex-wrap items-center justify-center gap-10 p-8 bg-gradient-to-r from-violet-50 via-fuchsia-50 to-indigo-50 dark:from-slate-900 dark:via-slate-900 dark:to-slate-900 rounded-[2.5rem] border border-slate-100 dark:border-slate-800">
                  <CircularProgress pct={s.overall_video_progress} color="#6366f1" size={110} stroke={8}/>
                  <CircularProgress pct={s.avg_exam_score} color="#10b981" size={110} stroke={8}/>
                  <CircularProgress pct={s.avg_book_progress} color="#f59e0b" size={110} stroke={8}/>
                  <div className="text-center lg:text-left max-w-xs">
                    <h3 className="text-lg font-black text-slate-900 dark:text-white">Overall Summary</h3>
                    <p className="text-sm text-slate-500 mt-1">
                      {s.total_courses} courses • {s.total_exams_taken} exams • {s.total_books} books
                    </p>
                  </div>
                </div>

                {/* ── COURSES / VIDEO ── */}
                {progress.courses.length > 0 && (
                  <section className="space-y-6">
                    <h2 className="text-2xl font-black text-slate-900 dark:text-white flex items-center gap-3">
                      <BookOpen size={24} className="text-indigo-600"/> Course & Video Progress
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {progress.courses.map(c => (
                        <motion.div key={c.course_id} whileHover={{y:-3}}
                          className="p-6 bg-white dark:bg-slate-900 rounded-[2rem] border border-slate-100 dark:border-slate-800 shadow-sm">
                          <div className="flex items-center gap-4 mb-4">
                            {c.thumbnail && <img src={c.thumbnail} alt="" className="w-14 h-14 rounded-xl object-cover"/>}
                            <div className="flex-1 min-w-0">
                              <h4 className="font-black text-sm text-slate-900 dark:text-white truncate">{c.course_title}</h4>
                              <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">
                                {c.completed_lessons}/{c.total_lessons} lessons
                              </p>
                            </div>
                            <span className={`text-sm font-black ${c.percentage>=80?'text-emerald-500':c.percentage>=50?'text-amber-500':'text-rose-500'}`}>
                              {c.percentage}%
                            </span>
                          </div>
                          <div className="w-full bg-slate-100 dark:bg-slate-800 rounded-full h-2.5 overflow-hidden">
                            <motion.div initial={{width:0}} animate={{width:`${c.percentage}%`}} transition={{duration:1}}
                              className={`h-full rounded-full ${c.percentage>=80?'bg-emerald-500':c.percentage>=50?'bg-amber-500':'bg-rose-500'}`}/>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </section>
                )}

                {/* ── EXAM RESULTS ── */}
                {progress.exams.length > 0 && (
                  <section className="space-y-6">
                    <h2 className="text-2xl font-black text-slate-900 dark:text-white flex items-center gap-3">
                      <BarChart3 size={24} className="text-emerald-500"/> Exam Performance
                    </h2>
                    <div className="bg-white dark:bg-slate-900 rounded-[2rem] border border-slate-100 dark:border-slate-800 overflow-hidden shadow-sm">
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-slate-100 dark:border-slate-800">
                              <th className="text-left px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Exam</th>
                              <th className="text-center px-4 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Score</th>
                              <th className="text-center px-4 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Percentage</th>
                              <th className="text-center px-4 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Status</th>
                              <th className="text-right px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Date</th>
                            </tr>
                          </thead>
                          <tbody>
                            {progress.exams.map(ex => (
                              <tr key={ex.submission_id} className="border-b border-slate-50 dark:border-slate-800/50 hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors">
                                <td className="px-6 py-4 font-bold text-slate-900 dark:text-white">{ex.exam_title}</td>
                                <td className="px-4 py-4 text-center font-black text-slate-700 dark:text-slate-300">
                                  {ex.score}/{ex.total_questions}
                                </td>
                                <td className="px-4 py-4 text-center">
                                  <span className={`inline-flex px-3 py-1 rounded-full text-xs font-black ${
                                    ex.percentage>=80?'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30':
                                    ex.percentage>=50?'bg-amber-50 text-amber-600 dark:bg-amber-900/30':
                                    'bg-rose-50 text-rose-600 dark:bg-rose-900/30'
                                  }`}>{ex.percentage}%</span>
                                </td>
                                <td className="px-4 py-4 text-center">
                                  {ex.passed
                                    ? <span className="inline-flex items-center gap-1 text-emerald-600 text-xs font-black"><CheckCircle2 size={14}/> Passed</span>
                                    : <span className="inline-flex items-center gap-1 text-rose-500 text-xs font-black"><XCircle size={14}/> Failed</span>}
                                </td>
                                <td className="px-6 py-4 text-right text-xs text-slate-400 font-bold">
                                  {ex.submitted_at ? new Date(ex.submitted_at).toLocaleDateString() : "—"}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </section>
                )}

                {/* ── BOOK PROGRESS ── */}
                {progress.books.length > 0 && (
                  <section className="space-y-6">
                    <h2 className="text-2xl font-black text-slate-900 dark:text-white flex items-center gap-3">
                      <Library size={24} className="text-amber-500"/> Book Reading Progress
                    </h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                      {progress.books.map(b => (
                        <motion.div key={b.book_id} whileHover={{y:-3}}
                          className="p-6 bg-white dark:bg-slate-900 rounded-[2rem] border border-slate-100 dark:border-slate-800 shadow-sm">
                          <div className="flex items-start gap-4 mb-4">
                            {b.thumbnail && <img src={b.thumbnail} alt="" className="w-12 h-16 rounded-lg object-cover shadow"/>}
                            <div className="flex-1 min-w-0">
                              <h4 className="font-black text-sm text-slate-900 dark:text-white line-clamp-1">{b.book_title}</h4>
                              <p className="text-[10px] text-slate-400 font-bold mt-1">By {b.author}</p>
                              <p className="text-[10px] text-slate-400 font-bold">{b.pages_read}/{b.total_pages} pages</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="flex-1 bg-slate-100 dark:bg-slate-800 rounded-full h-2 overflow-hidden">
                              <motion.div initial={{width:0}} animate={{width:`${b.percentage}%`}} transition={{duration:1}}
                                className="h-full rounded-full bg-amber-500"/>
                            </div>
                            <span className="text-xs font-black text-amber-600">{b.percentage}%</span>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </section>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                   {/* ── WEAK SUBJECTS ── */}
                   <section className="p-8 bg-amber-50/30 dark:bg-slate-900 border-2 border-dashed border-amber-100 dark:border-slate-800 rounded-[2.5rem]">
                      <h3 className="text-xl font-black text-slate-900 dark:text-white flex items-center gap-3 mb-6">
                         <BrainCircuit size={24} className="text-amber-600" /> Attention Required
                      </h3>
                      {progress.weak_subjects && progress.weak_subjects.length > 0 ? (
                         <div className="flex flex-wrap gap-2">
                            {progress.weak_subjects.map((ws, i) => (
                               <span key={i} className="px-4 py-2 bg-white dark:bg-slate-800 shadow-sm rounded-xl text-sm font-bold text-amber-700 dark:text-amber-400 border dark:border-slate-700">
                                  ⚠️ {ws}
                               </span>
                            ))}
                         </div>
                      ) : (
                         <p className="text-sm text-slate-500 italic">No significant learning weak-spots reported yet.</p>
                      )}
                   </section>

                   {/* ── TEACHER REMARKS ── */}
                   <section className="p-8 bg-blue-50/30 dark:bg-slate-900 border border-blue-100 dark:border-slate-800 rounded-[2.5rem]">
                      <h3 className="text-xl font-black text-slate-900 dark:text-white flex items-center gap-3 mb-6">
                         <MessageSquareQuote size={24} className="text-blue-600" /> Teacher Remarks
                      </h3>
                      <div className="space-y-4">
                         {progress.remarks && progress.remarks.length > 0 ? progress.remarks.map((rem, i) => (
                            <div key={i} className="bg-white dark:bg-slate-800 p-4 rounded-2xl border dark:border-slate-700 shadow-sm">
                               <p className="text-sm font-medium text-slate-700 dark:text-slate-300 italic">"{rem.feedback}"</p>
                               <p className="text-[10px] font-black text-slate-400 uppercase mt-2 tracking-wide">{rem.date ? new Date(rem.date).toLocaleDateString() : "Recent"}</p>
                            </div>
                         )) : (
                            <p className="text-sm text-slate-500 italic">Waiting for new educator feedback.</p>
                         )}
                      </div>
                   </section>
                </div>

                {/* ── PAYMENTS / FEES ── */}
                <section className="space-y-6">
                   <h2 className="text-2xl font-black text-slate-900 dark:text-white flex items-center gap-3">
                      <CreditCard size={24} className="text-emerald-500" /> Fees & Payment Status
                   </h2>
                   <div className="bg-white dark:bg-slate-900 rounded-[2rem] border border-slate-100 dark:border-slate-800 overflow-hidden shadow-sm">
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                              <tr className="border-b bg-slate-50 dark:bg-slate-800/50">
                                <th className="text-left px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Course</th>
                                <th className="text-center px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Amount</th>
                                <th className="text-center px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Status</th>
                                <th className="text-right px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Date</th>
                              </tr>
                          </thead>
                          <tbody>
                              {progress.payments && progress.payments.length > 0 ? progress.payments.map((pay, i) => (
                                <tr key={i} className="border-b dark:border-slate-800/50">
                                    <td className="px-6 py-4 font-bold">{pay.course}</td>
                                    <td className="px-6 py-4 text-center font-black text-slate-900 dark:text-white">₹{pay.amount}</td>
                                    <td className="px-6 py-4 text-center"><span className="px-3 py-1 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 rounded-full text-xs font-black">PAID</span></td>
                                    <td className="px-6 py-4 text-right text-xs text-slate-500 font-medium">{pay.date ? new Date(pay.date).toLocaleDateString() : "—"}</td>
                                </tr>
                              )) : (
                                <tr><td colSpan={4} className="px-6 py-8 text-center text-slate-500 italic">No payments recorded.</td></tr>
                              )}
                          </tbody>
                        </table>
                      </div>
                   </div>
                </section>

                {/* Empty states */}
                {progress.courses.length === 0 && progress.exams.length === 0 && progress.books.length === 0 && (
                  <div className="text-center py-16 bg-slate-50 dark:bg-slate-900 rounded-[2.5rem] border border-dashed border-slate-200 dark:border-slate-800">
                    <AlertCircle size={48} className="mx-auto text-slate-300 mb-4"/>
                    <h3 className="font-black text-lg text-slate-700 dark:text-slate-300">No Activity Yet</h3>
                    <p className="text-slate-400 text-sm mt-2">This student hasn't started any courses, exams, or books.</p>
                  </div>
                )}
              </motion.div>
            ) : null}
          </>
        )}
      </main>

      {/* ── LINK STUDENT MODAL ── */}
      <AnimatePresence>
        {showLinkModal && (
          <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowLinkModal(false)}>
            <motion.div initial={{scale:0.9,opacity:0}} animate={{scale:1,opacity:1}} exit={{scale:0.9,opacity:0}}
              className="bg-white dark:bg-slate-900 rounded-[2rem] p-8 max-w-md w-full shadow-2xl border border-slate-100 dark:border-slate-800"
              onClick={e => e.stopPropagation()}>
              <div className="mb-6">
                <div className="w-14 h-14 bg-violet-100 dark:bg-violet-900/30 rounded-2xl flex items-center justify-center mb-4">
                  <UserPlus className="text-violet-600" size={24}/>
                </div>
                <h3 className="text-2xl font-black text-slate-900 dark:text-white">Link a Student</h3>
                <p className="text-sm text-slate-500 mt-1">Enter your child's student email address.</p>
              </div>
              <form onSubmit={handleLink} className="space-y-4">
                <input type="email" required placeholder="student@email.com" value={linkEmail}
                  onChange={e => setLinkEmail(e.target.value)}
                  className="w-full px-5 py-4 bg-slate-50 dark:bg-slate-800 border-0 rounded-2xl text-slate-800 dark:text-white focus:ring-2 focus:ring-violet-500/50 outline-none"/>
                <div className="flex gap-3">
                  <button type="button" onClick={() => setShowLinkModal(false)}
                    className="flex-1 py-4 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 font-black rounded-2xl hover:bg-slate-200 transition-all">
                    Cancel
                  </button>
                  <button type="submit" disabled={linkLoading}
                    className="flex-1 py-4 bg-violet-600 hover:bg-violet-700 text-white font-black rounded-2xl shadow-lg shadow-violet-200 dark:shadow-none transition-all disabled:opacity-60">
                    {linkLoading ? <Loader2 className="animate-spin mx-auto" size={20}/> : "Link Student"}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
